import { mdiClose } from '@mdi/js';
import Icon from '@mdi/react';
import { Box, Button, IconButton, Paper, Popper, Snackbar, Stack, Typography } from '@mui/material';
import {
  KeyboardEvent as ReactKeyboardEvent,
  RefObject,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';

import { Colors } from '../../shared/colors';

export interface GuidedTourStep {
  title: string;
  description: string;
  target: RefObject<HTMLElement>;
}

interface Props {
  open: boolean;
  steps: GuidedTourStep[];
  onClose: () => void;
  onComplete: () => void;
  activeStep?: number;
  onStepChange?: (index: number) => void;
  label?: string;
}

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const SPOTLIGHT_PADDING = 6;
const AVISO_EXIBIDO_KEY = 'guidedTour:avisoExibido';

const avisoJaExibido = () => {
  try {
    if (sessionStorage.getItem(AVISO_EXIBIDO_KEY)) return true;
    sessionStorage.setItem(AVISO_EXIBIDO_KEY, '1');
  } catch {
    return false;
  }
  return false;
};

const GuidedTour = ({
  open,
  steps,
  onClose,
  onComplete,
  activeStep,
  onStepChange,
  label
}: Props) => {
  const [stepIndex, setStepIndex] = useState(0);
  const currentStepIndex = activeStep ?? stepIndex;
  const [spotlight, setSpotlight] = useState<SpotlightRect | null>(null);
  const [arrowEl, setArrowEl] = useState<HTMLSpanElement | null>(null);
  const [avisoAberto, setAvisoAberto] = useState(false);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();
  const step = steps[currentStepIndex];
  const target = step?.target.current;
  const spotlightReady = Boolean(spotlight);

  useEffect(() => {
    if (!open) return undefined;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    return () => {
      if (previousFocusRef.current?.isConnected) previousFocusRef.current.focus();
    };
  }, [open]);

  useEffect(() => {
    if (open) setAvisoAberto(false);
    else setStepIndex(0);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !target) {
      setSpotlight(null);
      return undefined;
    }

    const updateSpotlight = () => {
      const rect = target.getBoundingClientRect();
      const top = Math.max(0, rect.top - SPOTLIGHT_PADDING);
      const left = Math.max(0, rect.left - SPOTLIGHT_PADDING);
      setSpotlight({
        top,
        left,
        width: Math.min(window.innerWidth, rect.right + SPOTLIGHT_PADDING) - left,
        height: Math.min(window.innerHeight, rect.bottom + SPOTLIGHT_PADDING) - top
      });
    };

    const initialRect = target.getBoundingClientRect();
    if (initialRect.top < 16 || initialRect.bottom > window.innerHeight - 16) {
      target.scrollIntoView({ block: 'center' });
    }
    updateSpotlight();

    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateSpotlight);
    observer?.observe(target);
    window.addEventListener('resize', updateSpotlight);
    window.addEventListener('scroll', updateSpotlight, true);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateSpotlight);
      window.removeEventListener('scroll', updateSpotlight, true);
    };
  }, [open, target]);

  useEffect(() => {
    if (open && spotlightReady) dialogRef.current?.focus({ preventScroll: true });
  }, [open, spotlightReady, currentStepIndex]);

  // O Popper monta o balão depois do efeito acima; o foco entra quando ele aparece.
  const setDialogRef = useCallback((node: HTMLDivElement | null) => {
    dialogRef.current = node;
    node?.focus({ preventScroll: true });
  }, []);

  const aviso = (
    <Snackbar
      open={avisoAberto}
      autoHideDuration={5000}
      onClose={() => setAvisoAberto(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      message="Quando quiser rever, é só abrir a Ajuda."
      ContentProps={{ sx: { bgcolor: Colors.text.primary, fontSize: 15 } }}
    />
  );

  if (!open || !step || !target || !spotlight) return aviso;

  const fechar = () => {
    if (!avisoJaExibido()) setAvisoAberto(true);
    onClose();
  };

  const concluir = () => {
    if (!avisoJaExibido()) setAvisoAberto(true);
    onComplete();
  };

  const isLastStep = currentStepIndex === steps.length - 1;

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      fechar();
    }
    if (event.key !== 'Tab' || !dialogRef.current) return;

    const buttons = dialogRef.current.querySelectorAll<HTMLButtonElement>('button:not(:disabled)');
    const first = buttons[0];
    const last = buttons[buttons.length - 1];
    if (!first || !last) return;

    if (
      event.shiftKey &&
      (document.activeElement === first || document.activeElement === dialogRef.current)
    ) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const card = (
    <Paper
      ref={setDialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      elevation={8}
      sx={{
        position: 'relative',
        width: 360,
        maxWidth: 'calc(100vw - 32px)',
        boxSizing: 'border-box',
        p: 2.5,
        borderRadius: '12px',
        border: `1px solid ${Colors.border.divider}`,
        backgroundColor: Colors.white,
        color: Colors.text.primary
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
        <Typography
          fontSize={12}
          fontWeight={500}
          letterSpacing={0.4}
          textTransform="uppercase"
          color={Colors.primary.dark}
        >
          {label ? `${label} · ` : ''}
          {currentStepIndex + 1} de {steps.length}
        </Typography>
        <IconButton
          aria-label="Fechar ajuda"
          onClick={fechar}
          size="small"
          sx={{ mr: -1, mt: -0.5, color: 'secondary.main' }}
        >
          <Icon path={mdiClose} size={0.8} />
        </IconButton>
      </Stack>
      <Typography id={titleId} fontSize={18} fontWeight={700} color={Colors.secondary} mt={0.5}>
        {step.title}
      </Typography>
      <Typography id={descriptionId} variant="body1" mt={1} lineHeight={1.5}>
        {step.description}
      </Typography>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} mt={2.5}>
        <Box aria-hidden="true" display="flex" gap={0.75}>
          {steps.length > 1 &&
            steps.map((item, index) => (
              <Box
                key={item.title}
                width={index === currentStepIndex ? 18 : 6}
                height={6}
                borderRadius="3px"
                bgcolor={index === currentStepIndex ? Colors.primary.dark : Colors.border.input}
              />
            ))}
        </Box>
        <Stack direction="row" gap={0.5}>
          {currentStepIndex > 0 && (
            <Button
              onClick={() => {
                if (onStepChange) onStepChange(currentStepIndex - 1);
                else setStepIndex(currentStepIndex - 1);
              }}
              sx={{ color: 'primary.dark', fontWeight: 500, textTransform: 'none' }}
            >
              Voltar
            </Button>
          )}
          <Button
            variant="contained"
            disableElevation
            sx={{
              backgroundColor: 'primary.dark',
              borderRadius: '8px',
              fontWeight: 500,
              px: 2.5,
              textTransform: 'none'
            }}
            onClick={() => {
              if (isLastStep) concluir();
              else if (onStepChange) onStepChange(currentStepIndex + 1);
              else setStepIndex(currentStepIndex + 1);
            }}
          >
            {isLastStep ? 'Entendi' : 'Próximo'}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );

  return (
    <>
      {createPortal(
        <Box
          aria-hidden="true"
          onClick={fechar}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: (theme) => theme.zIndex.modal + 1,
            backgroundColor: 'transparent'
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              ...spotlight,
              border: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 1,
              boxSizing: 'border-box',
              boxShadow: `0 0 0 3px ${Colors.white}, 0 0 0 100vmax rgba(16, 42, 86, 0.5)`,
              pointerEvents: 'none'
            }}
          />
        </Box>,
        document.body
      )}
      <Popper
        open
        anchorEl={target}
        placement="bottom-start"
        modifiers={[
          { name: 'offset', options: { offset: [0, 14] } },
          { name: 'preventOverflow', options: { padding: 16 } },
          { name: 'flip', options: { padding: 16 } },
          { name: 'arrow', enabled: true, options: { element: arrowEl, padding: 14 } }
        ]}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 2,
          '&[data-popper-placement*="bottom"] .guided-tour-arrow': { top: -7 },
          '&[data-popper-placement*="top"] .guided-tour-arrow': { bottom: -7 }
        }}
      >
        {card}
        <Box
          component="span"
          ref={setArrowEl}
          className="guided-tour-arrow"
          aria-hidden="true"
          sx={{
            position: 'absolute',
            width: 14,
            height: 14,
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              backgroundColor: Colors.white,
              transform: 'rotate(45deg)',
              borderRadius: '2px'
            }
          }}
        />
      </Popper>
      {aviso}
    </>
  );
};

export default GuidedTour;
